/* =====================================================
   Feedback Section Component – Version 2.1 (Modified: July 31, 2025)
   Shows equation results, validation messages, and optimal solution
   Date Modified: 31 July 2025 | Time: Current
===================================================== */

import { memo, useCallback } from 'react'
import { useGameState } from '../hooks/useGameState'
import { formatEquation } from '../utils/gameLogic'
import styles from './FeedbackSection.module.css'

/**
 * Feedback section displaying game results and solution
 */
function FeedbackSection() {
  const { 
    feedback, 
    tracker, 
    showBestSolution, 
    setShowBestSolution, 
    bestEquation 
  } = useGameState()

  const toggleBestSolution = useCallback(() => {
    setShowBestSolution(prev => !prev)
  }, [setShowBestSolution])

  return (
    <div className={styles.feedbackSection}>
      <h4>Feedback & Solution</h4>
      
      <BestSolutionToggle 
        showBestSolution={showBestSolution}
        onToggle={toggleBestSolution}
      />
      
      {showBestSolution && (
        <BestSolutionDisplay equation={bestEquation} />
      )}
      
      <div className={styles.feedbackGroup}>
        <FeedbackMessage message={feedback.message} color={feedback.color} />
        <EquationTracker content={tracker} />
      </div>
    </div>
  )
}

/**
 * Toggle button for showing/hiding the optimal solution
 */
const BestSolutionToggle = memo(function BestSolutionToggle({ 
  showBestSolution, 
  onToggle 
}) {
  return (
    <button 
      className={styles.toggleBestButton}
      onClick={onToggle}
    >
      {showBestSolution ? 'Hide Best Solution' : 'Show Best Solution'}
    </button>
  )
})

/**
 * Display component for the optimal solution
 */
const BestSolutionDisplay = memo(function BestSolutionDisplay({ equation }) {
  return (
    <div className={styles.bestEquationDisplay}>
      🔍 Best Solution: {formatEquation(equation)}
    </div>
  )
})

/**
 * Feedback message component
 */
const FeedbackMessage = memo(function FeedbackMessage({ message, color }) {
  if (!message) return null

  return (
    <div 
      className={styles.feedback}
      style={{ color }}
    >
      {message}
    </div>
  )
})

/**
 * Equation tracking component showing calculation steps
 */
const EquationTracker = memo(function EquationTracker({ content }) {
  if (!content) return null

  return (
    <div className={styles.tracker}>
      {content}
    </div>
  )
})

export default FeedbackSection